import React, { useEffect } from 'react';
import { View, Text, Pressable } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import * as Haptics from 'expo-haptics';
import AnimatedPressable from '@/components/AnimatedPressable';
import { isWeb } from '@/lib/platform';
import {
  TrendingUp,
  TrendingDown,
  ChevronRight,
  Eye,
  Heart,
  FolderOpen,
  Activity,
} from 'lucide-react-native';
import Animated, {
  FadeInUp,
  FadeIn,
  SlideInRight,
  useSharedValue,
  useAnimatedStyle,
  withTiming,
  withRepeat,
  withDelay,
  withSequence,
  Easing,
  interpolate,
} from 'react-native-reanimated';
import Svg, { Circle, Defs, LinearGradient as SvgLinearGradient, Stop } from 'react-native-svg';
import { useThemeColors } from '@/lib/useThemeColors';

// ─── ScoreRing ──────────────────────────────────────────────────
export function ScoreRing({ score, size = 180 }: { score: number; size?: number }) {
  const colors = useThemeColors();
  const progress = useSharedValue(0);
  const strokeWidth = 12;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;

  useEffect(() => {
    progress.value = withDelay(
      300,
      withTiming(score / 100, {
        duration: 1500,
        easing: Easing.out(Easing.cubic),
      })
    );
  }, [score]);

  const animatedProps = useAnimatedStyle(() => {
    return {
      opacity: interpolate(progress.value, [0, 0.1], [0.5, 1]),
    };
  });

  const strokeDashoffset = circumference * (1 - score / 100);

  return (
    <Animated.View style={[{ width: size, height: size }, animatedProps]}>
      <Svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <Defs>
          <SvgLinearGradient id="scoreGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <Stop offset="0%" stopColor={colors.primary[400]} />
            <Stop offset="50%" stopColor={colors.primary[500]} />
            <Stop offset="100%" stopColor={colors.primary[600]} />
          </SvgLinearGradient>
        </Defs>
        {/* Background Circle */}
        <Circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={colors.border.light}
          strokeWidth={strokeWidth}
          fill="none"
        />
        {/* Progress Circle */}
        <Circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="url(#scoreGradient)"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          fill="none"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </Svg>
      {/* Center Content */}
      <View
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Text
          className="text-5xl font-bold"
          style={{
            color: colors.text.primary,
            letterSpacing: -2,
          }}
        >
          {score}
        </Text>
        <Text
          className="text-sm font-medium"
          style={{
            color: colors.text.tertiary,
            marginTop: -4,
          }}
        >
          SCORE
        </Text>
      </View>
    </Animated.View>
  );
}

// ─── MiniSparkline ──────────────────────────────────────────────
export function MiniSparkline({ data, color, height = 32 }: { data: number[]; color: string; height?: number }) {
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;

  return (
    <View style={{ flexDirection: 'row', alignItems: 'flex-end', height, gap: 2 }}>
      {data.map((value, index) => {
        const barHeight = ((value - min) / range) * height * 0.8 + height * 0.2;
        return (
          <View
            key={index}
            style={{
              width: 4,
              height: barHeight,
              backgroundColor: index === data.length - 1 ? color : `${color}40`,
              borderRadius: 2,
            }}
          />
        );
      })}
    </View>
  );
}

// ─── PulseDot ───────────────────────────────────────────────────
export function PulseDot({ color, isOnline }: { color: string; isOnline: boolean }) {
  const scale = useSharedValue(1);

  useEffect(() => {
    if (isOnline) {
      scale.value = withRepeat(
        withSequence(
          withTiming(1.3, { duration: 1000 }),
          withTiming(1, { duration: 1000 })
        ),
        -1,
        false
      );
    }
  }, [isOnline]);

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ scale: scale.value }],
  }));

  if (!isOnline) return null;

  return (
    <View style={{ position: 'absolute', top: -2, right: -2 }}>
      <Animated.View
        style={[
          {
            width: 10,
            height: 10,
            borderRadius: 5,
            backgroundColor: color,
          },
          animatedStyle,
        ]}
      />
    </View>
  );
}

// ─── StatCard ───────────────────────────────────────────────────
interface StatCardProps {
  icon: React.ComponentType<{ size: number; color: string }>;
  iconBgColor: string;
  iconColor: string;
  value: string | number;
  label: string;
  trend?: number;
  trendLabel?: string;
  onPress?: () => void;
  delay?: number;
}

export function StatCard({
  icon: Icon,
  iconBgColor,
  iconColor,
  value,
  label,
  trend,
  trendLabel,
  onPress,
  delay = 0,
}: StatCardProps) {
  const colors = useThemeColors();
  const showTrend = trend !== undefined || (trendLabel && trend === undefined);
  const trendColor = trend === undefined
    ? colors.text.tertiary
    : trend >= 0 ? colors.success : colors.error;
  const TrendIcon = trend === undefined ? null : trend >= 0 ? TrendingUp : TrendingDown;

  return (
    <Animated.View entering={FadeInUp.delay(delay).duration(500)} style={{ flex: 1 }}>
      <AnimatedPressable
        onPress={onPress}
        style={{
          flex: 1,
          flexDirection: 'row',
          alignItems: 'center',
          gap: 12,
          backgroundColor: colors.surface.primary,
          borderRadius: 16,
          padding: 14,
          shadowColor: '#000',
          shadowOpacity: 0.05,
          shadowRadius: 10,
          shadowOffset: { width: 0, height: 2 },
          elevation: 2,
        }}
      >
        {/* Icon block (left) */}
        <View
          style={{
            width: 40,
            height: 40,
            borderRadius: 12,
            backgroundColor: iconBgColor,
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Icon size={20} color={iconColor} />
        </View>

        {/* Content — horizontal on web, stacked on mobile (trend pill below) */}
        <View
          style={{
            flex: 1,
            minWidth: 0,
            flexDirection: isWeb ? 'row' : 'column',
            alignItems: isWeb ? 'center' : 'flex-start',
            gap: isWeb ? 0 : 4,
          }}
        >
          <View style={{ flex: isWeb ? 1 : undefined, minWidth: 0, alignSelf: 'stretch' }}>
            <Text
              style={{
                color: colors.text.primary,
                fontSize: isWeb ? 22 : 20,
                fontWeight: '700',
                letterSpacing: -0.6,
                lineHeight: isWeb ? 26 : 22,
              }}
              numberOfLines={1}
            >
              {value}
            </Text>
            <Text
              style={{
                color: colors.text.tertiary,
                fontSize: 11,
                fontWeight: '500',
                marginTop: 1,
                lineHeight: 14,
              }}
              numberOfLines={2}
            >
              {label}
            </Text>
          </View>
          {showTrend && (
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: 3,
                paddingHorizontal: 8,
                paddingVertical: 4,
                borderRadius: 999,
                backgroundColor:
                  trend !== undefined && trend >= 0
                    ? colors.traffic.greenLight
                    : trend !== undefined
                      ? colors.red.light
                      : colors.surface.secondary,
                marginLeft: isWeb ? 8 : 0,
                flexShrink: 0,
              }}
            >
              {TrendIcon && <TrendIcon size={10} color={trendColor} />}
              <Text
                style={{
                  color: trendColor,
                  fontSize: 10,
                  fontWeight: '700',
                }}
                numberOfLines={1}
              >
                {trend !== undefined && trend > 0 ? '+' : ''}
                {trendLabel ?? (trend !== undefined ? String(trend) : '')}
              </Text>
            </View>
          )}
        </View>
      </AnimatedPressable>
    </Animated.View>
  );
}

// ─── ActionItem ─────────────────────────────────────────────────
interface ActionItemProps {
  icon: React.ComponentType<{ size: number; color: string }>;
  iconBgColors: [string, string];
  title: string;
  subtitle: string;
  badge?: number;
  onPress?: () => void;
  delay?: number;
}

export function ActionItem({
  icon: Icon,
  iconBgColors,
  title,
  subtitle,
  badge,
  onPress,
  delay = 0,
}: ActionItemProps) {
  const colors = useThemeColors();

  const handlePress = () => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    onPress?.();
  };

  return (
    <Animated.View entering={SlideInRight.delay(delay).duration(400)}>
      <Pressable
        onPress={handlePress}
        android_ripple={{ color: colors.surface.secondary }}
        style={{ borderRadius: 16 }}
      >
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            paddingVertical: 12,
            paddingHorizontal: 4,
          }}
        >
          <LinearGradient
            colors={iconBgColors}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={{
              width: 44,
              height: 44,
              borderRadius: 14,
              justifyContent: 'center',
              alignItems: 'center',
              marginRight: 14,
              flexShrink: 0,
            }}
          >
            <Icon size={22} color="#fff" />
          </LinearGradient>
          <View style={{ flex: 1, minWidth: 0 }}>
            <Text
              className="text-base font-semibold"
              style={{ color: colors.text.primary }}
              numberOfLines={1}
            >
              {title}
            </Text>
            <Text
              className="text-sm mt-0.5"
              style={{ color: colors.text.tertiary }}
              numberOfLines={1}
            >
              {subtitle}
            </Text>
          </View>
          {badge !== undefined && badge > 0 && (
            <View
              style={{
                backgroundColor: colors.error,
                paddingHorizontal: 8,
                paddingVertical: 3,
                borderRadius: 10,
                marginLeft: 8,
                flexShrink: 0,
              }}
            >
              <Text className="text-xs font-bold text-white">{badge}</Text>
            </View>
          )}
          <ChevronRight
            size={18}
            color={colors.text.tertiary}
            style={{ marginLeft: 8, flexShrink: 0 }}
          />
        </View>
      </Pressable>
    </Animated.View>
  );
}

// ─── ActivityItem ───────────────────────────────────────────────
interface ActivityItemActivity {
  id: string;
  type: 'view' | 'interest' | 'dataroom';
  investorName: string;
  investorFirm: string;
  action: string;
  time: string;
  isOnline: boolean;
}

interface ActivityItemProps {
  activity: ActivityItemActivity;
  delay?: number;
}

export function ActivityItem({ activity, delay = 0 }: ActivityItemProps) {
  const colors = useThemeColors();

  const getActivityIcon = () => {
    switch (activity.type) {
      case 'view':
        return { icon: Eye, color: colors.primary[500], bg: colors.primary[50] };
      case 'interest':
        return { icon: Heart, color: colors.success, bg: colors.traffic.greenLight };
      case 'dataroom':
        return { icon: FolderOpen, color: colors.warning, bg: colors.amber.light };
      default:
        return { icon: Activity, color: colors.text.tertiary, bg: colors.surface.secondary };
    }
  };

  const { icon: Icon, color, bg } = getActivityIcon();

  return (
    <Animated.View entering={FadeIn.delay(delay).duration(400)}>
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          paddingVertical: 12,
        }}
      >
        <View style={{ position: 'relative' }}>
          <View
            style={{
              width: 44,
              height: 44,
              borderRadius: 22,
              backgroundColor: bg,
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Icon size={20} color={color} />
          </View>
          <PulseDot color={colors.success} isOnline={activity.isOnline} />
        </View>
        <View style={{ flex: 1, marginLeft: 12 }}>
          <Text
            className="text-sm font-semibold"
            style={{ color: colors.text.primary }}
          >
            {activity.investorName}
          </Text>
          <Text
            className="text-sm mt-px"
            style={{ color: colors.text.secondary }}
          >
            {activity.investorFirm}
          </Text>
        </View>
        <View style={{ alignItems: 'flex-end' }}>
          <Text
            className="text-xs font-medium"
            style={{ color: colors.text.tertiary }}
          >
            {activity.time}
          </Text>
          <Text
            className="text-xs mt-0.5"
            style={{ color: colors.primary[500] }}
          >
            {activity.action.split(' ').slice(0, 2).join(' ')}
          </Text>
        </View>
      </View>
    </Animated.View>
  );
}
